import { useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

// Only create Supabase client if environment variables are available and valid
let supabase = null;
let supabaseEnabled = false;

try {
  if (supabaseUrl && supabaseAnonKey && supabaseUrl.includes('supabase.co')) {
    supabase = createClient(supabaseUrl, supabaseAnonKey);
    supabaseEnabled = true;
  }
} catch (error) {
  console.warn('Supabase analytics disabled:', error);
  supabase = null;
  supabaseEnabled = false;
}

// Generate a session ID that persists for the browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

interface AnalyticsEvent {
  page_path: string;
  page_title: string;
  event_type: 'page_view' | 'scroll_depth' | 'time_on_page' | 'section_view' | 'pdf_download';
  session_id: string;
  user_agent: string;
  referrer: string;
  download_url?: string;
}

const trackEvent = async (event: Omit<AnalyticsEvent, 'session_id' | 'user_agent' | 'referrer'>) => {
  // Skip tracking if Supabase is not configured
  if (!supabase || !supabaseEnabled) {
    return; // Silently skip without console warnings
  }

  try {
    const { error } = await supabase.from('whitepaper_analytics').insert({
      ...event,
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer || 'direct'
    });
    
    // If we get an auth error, disable Supabase for this session
    if (error && (error.message?.includes('authorization') || error.message?.includes('JWT'))) {
      supabaseEnabled = false;
      console.warn('Analytics disabled due to authorization error');
    }
  } catch (error) {
    // Silently fail - don't break the page
    supabaseEnabled = false;
  }
};

export const useAnalytics = (pageTitle: string) => {
  const startTimeRef = useRef<number>(Date.now());
  const scrollDepthTrackedRef = useRef<Set<number>>(new Set());
  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    const pagePath = window.location.pathname;

    // Track page view (only once per mount)
    if (!hasTrackedViewRef.current) {
      trackEvent({
        page_path: pagePath,
        page_title: pageTitle,
        event_type: 'page_view'
      });
      hasTrackedViewRef.current = true;
    }

    // Track scroll depth
    const handleScroll = () => {
      const scrollPercentage = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      // Track at 25%, 50%, 75%, and 100%
      const milestones = [25, 50, 75, 100];
      milestones.forEach(milestone => {
        if (scrollPercentage >= milestone && !scrollDepthTrackedRef.current.has(milestone)) {
          scrollDepthTrackedRef.current.add(milestone);
          trackEvent({
            page_path: pagePath,
            page_title: `${pageTitle} - ${milestone}% scrolled`,
            event_type: 'scroll_depth'
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Track time on page when leaving
    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000); // in seconds
      if (timeSpent > 5) { // Only track if spent more than 5 seconds
        trackEvent({
          page_path: pagePath,
          page_title: `${pageTitle} - ${timeSpent}s`,
          event_type: 'time_on_page'
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pageTitle]);

  // Function to track custom section views
  const trackSectionView = (sectionName: string) => {
    trackEvent({
      page_path: window.location.pathname,
      page_title: `${pageTitle} - ${sectionName}`,
      event_type: 'section_view'
    });
  };

  // Function to track PDF downloads
  const trackDownload = (downloadUrl: string, fileName: string) => {
    trackEvent({
      page_path: window.location.pathname,
      page_title: `${pageTitle} - ${fileName}`,
      event_type: 'pdf_download',
      download_url: downloadUrl
    });
  };

  return { trackSectionView, trackDownload };
};
